import React, { useRef, useState } from 'react';
import { Upload, Image, RefreshCw, FilePlus, X } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для загрузки и предварительного просмотра изображений
 */
const ImageUploader = ({ onFileSelect, isLoading, error, resetAndLoadNew, exifData, imageUrl }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef(null);
  
  // Обработчики перетаскивания файлов
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(dragCounter + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(dragCounter - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      validateAndProcessFile(file);
      e.dataTransfer.clearData();
    }
  };
  
  // Открытие диалога выбора файла
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };
  
  // Обработка выбора файла через диалог
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      validateAndProcessFile(file);
    }
  };
  
  // Валидация и обработка выбранного файла
  const validateAndProcessFile = (file) => {
    // Проверка типа файла
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff', 'image/heic'];
    
    if (!validImageTypes.includes(file.type)) {
      alert('Пожалуйста, выберите файл изображения (JPEG, PNG, TIFF или HEIC)');
      return;
    }
    
    // Проверка размера файла (ограничение 30 МБ)
    const maxSize = 30 * 1024 * 1024; // 30 МБ
    if (file.size > maxSize) {
      alert('Размер файла превышает 30 МБ. Пожалуйста, выберите файл меньшего размера.');
      return;
    }
    
    // Передаем файл для обработки
    onFileSelect(file);
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
      {/* Заголовок секции */}
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Image className="w-5 h-5 mr-2 text-purple-500" />
        Анализатор EXIF-данных изображений
      </h2>
      
      {exifData && imageUrl ? (
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 mr-4 flex-shrink-0">
              <img 
                src={imageUrl} 
                alt="Предпросмотр" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/placeholder.png";
                }}
              />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {exifData.file.name}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {exifData.image.width} × {exifData.image.height} • {(exifData.file.size / (1024 * 1024)).toFixed(2)} МБ
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={resetAndLoadNew}
            icon={<RefreshCw className="w-4 h-4" />}
          >
            Загрузить другой файл
          </Button>
        </div>
      ) : (
        <div 
          className={`mt-2 p-6 border-2 border-dashed rounded-lg ${
            isDragging 
              ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20' 
              : 'border-gray-300 dark:border-gray-600'
          } transition-colors duration-200`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center py-4">
            <Upload className={`w-10 h-10 mb-4 ${
              isDragging ? 'text-purple-500' : 'text-gray-400 dark:text-gray-500'
            }`} />
            
            <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
              {isDragging ? 'Отпустите для загрузки' : 'Перетащите изображение сюда'}
            </h3>
            
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400 text-center">
              или <span className="text-purple-600 dark:text-purple-400 font-medium cursor-pointer hover:underline" onClick={handleBrowseClick}>выберите файл</span> для загрузки
              <br />
              <span className="text-xs">JPG, JPEG, PNG, TIFF, HEIC — до 30 МБ</span>
            </p>
            
            <input 
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpeg,image/jpg,image/png,image/tiff,image/heic"
              onChange={handleFileChange}
            />
            
            <Button
              primary
              size="md"
              onClick={handleBrowseClick}
              icon={<FilePlus className="w-4 h-4" />}
            >
              Выбрать файл
            </Button>
          </div>
        </div>
      )}
      
      {/* Индикатор загрузки */}
      {isLoading && (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 flex items-center">
          <div className="animate-spin mr-3">
            <RefreshCw className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Анализ EXIF-данных изображения...
          </p>
        </div>
      )}
      
      {/* Сообщение об ошибке */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800 flex items-start">
          <X className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
              Ошибка при анализе изображения
            </p>
            <p className="text-xs text-red-700 dark:text-red-400">
              {error}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={resetAndLoadNew}
            >
              Попробовать другой файл
            </Button>
          </div>
        </div>
      )}
      
      {/* Подсказка */}
      {!exifData && !isLoading && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <strong>Примечание:</strong> Анализ производится локально в вашем браузере. Ваши файлы не загружаются на сервер, обеспечивая полную приватность данных.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;